import { motion, useScroll, useTransform } from 'framer-motion';
import { useRef, useState } from 'react';
import { ChevronDown, Star, Heart, Zap, Shield, Leaf, Camera } from 'lucide-react';
import { IMAGES } from '@/assets/images';

// ── Scroll-to helper ──────────────────────────────────────────────────────────
const scrollTo = (id: string) =>
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });

// ── Animation variants ────────────────────────────────────────────────────────
const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.65, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};
const fadeIn = {
  hidden: { opacity: 0 },
  visible: (i = 0) => ({
    opacity: 1,
    transition: { duration: 0.5, delay: i * 0.1 },
  }),
};

// ── Data ──────────────────────────────────────────────────────────────────────
const FACTS = [
  {
    icon: <Star className="w-7 h-7" />,
    title: '百兽之王',
    desc: '狮子是非洲草原上最具威严的大型猫科动物，被誉为"草原之王"。成年雄狮的鬃毛是力量与地位的象征。',
    color: 'from-amber-400/20 to-amber-600/10',
    border: 'border-amber-300/40',
  },
  {
    icon: <Heart className="w-7 h-7" />,
    title: '家族温情',
    desc: '狮子是猫科动物中唯一群居的物种，狮群由多头母狮和幼崽组成，彼此照顾、共同捕猎，充满温情。',
    color: 'from-rose-400/20 to-rose-600/10',
    border: 'border-rose-300/40',
  },
  {
    icon: <Zap className="w-7 h-7" />,
    title: '超凡速度',
    desc: '奔跑时速可达 80 km/h，短距冲刺爆发力惊人。幼崽从出生起便跟随母亲学习奔跑与捕猎技巧。',
    color: 'from-yellow-400/20 to-yellow-600/10',
    border: 'border-yellow-300/40',
  },
  {
    icon: <Shield className="w-7 h-7" />,
    title: '守护领地',
    desc: '雄狮负责巡逻守卫领地，吼声可在 8 公里外被听到。这震天的声音是对入侵者最有力的警告。',
    color: 'from-orange-400/20 to-orange-600/10',
    border: 'border-orange-300/40',
  },
  {
    icon: <Leaf className="w-7 h-7" />,
    title: '草原生态',
    desc: '狮子是非洲生态系统中的顶级捕食者，维持着草食动物的种群平衡，对整个草原生态至关重要。',
    color: 'from-green-400/20 to-green-600/10',
    border: 'border-green-300/40',
  },
  {
    icon: <Camera className="w-7 h-7" />,
    title: '可爱幼崽',
    desc: '刚出生的小狮子体重仅 1–2 kg，布满斑点，双眼初开时懵懵懂懂，是世界上最可爱的存在之一。',
    color: 'from-pink-400/20 to-pink-600/10',
    border: 'border-pink-300/40',
  },
];

const TIMELINE = [
  { age: '0–6 周', title: '睁眼看世界', desc: '小狮子出生时闭着眼，约两周后才缓缓睁开，全身布满迷彩斑点，依偎在妈妈怀中。', img: IMAGES.CUB_6 },
  { age: '2–4 月', title: '开始探索', desc: '腿脚渐稳，开始在妈妈身边跑跳嬉戏，与同伴玩耍中练习将来的捕猎技能。', img: IMAGES.CUB_3 },
  { age: '4–8 月', title: '随队捕猎', desc: '跟随狮群出行，远远观察母狮狩猎，好奇的大眼睛盯着每一个细节。', img: IMAGES.CUB_5 },
  { age: '1–2 岁', title: '少年时期', desc: '体型迅速增大，雄性开始长出稀疏鬃毛，逐渐展现王者风范。', img: IMAGES.CUB_8 },
  { age: '2–4 岁', title: '亚成年', desc: '独立意识增强，雄狮开始脱离出生狮群，寻找属于自己的领地与荣耀。', img: IMAGES.CUB_2 },
  { age: '4岁以上', title: '草原之王', desc: '鬃毛浓密威武，在广袤草原上建立自己的狮群，开始新一轮的生命传承。', img: IMAGES.CUB_4 },
];

const GALLERY = [
  { src: IMAGES.CUB_4,  label: '凝视' },
  { src: IMAGES.CUB_2,  label: '草野漫步' },
  { src: IMAGES.CUB_1,  label: '休憩' },
  { src: IMAGES.CUB_5,  label: '坐望草原' },
  { src: IMAGES.CUB_8,  label: '王者之姿' },
  { src: IMAGES.CUB_3,  label: '初探世界' },
  { src: IMAGES.SAVANNA_9, label: '草原金昏' },
  { src: IMAGES.CUB_10, label: '小可爱' },
  { src: IMAGES.SAVANNA_6, label: '黄昏草原' },
];

// ── Navbar ────────────────────────────────────────────────────────────────────
function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  if (typeof window !== 'undefined') {
    window.addEventListener('scroll', () => setScrolled(window.scrollY > 60), { passive: true });
  }
  return (
    <motion.nav
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: 'easeOut' }}
      className={`fixed top-0 left-0 right-0 z-50 flex items-center justify-between px-6 md:px-12 py-4 transition-all duration-300 ${
        scrolled ? 'bg-card/90 backdrop-blur-md shadow-md' : 'bg-transparent'
      }`}
    >
      <div className="flex items-center gap-2">
        <span className="text-2xl">🦁</span>
        <span
          className="text-xl font-black tracking-wide"
          style={{ fontFamily: "'Playfair Display', serif", color: 'var(--amber)' }}
        >
          小狮子王国
        </span>
      </div>
      <div className="hidden md:flex gap-8 text-sm font-semibold text-foreground/70">
        {[
          ['关于小狮', 'about'],
          ['趣味知识', 'facts'],
          ['成长历程', 'timeline'],
          ['图片展廊', 'gallery'],
        ].map(([label, id]) => (
          <button
            key={id}
            onClick={() => scrollTo(id)}
            className="hover:text-primary transition-colors"
          >
            {label}
          </button>
        ))}
      </div>
    </motion.nav>
  );
}

// ── Hero ──────────────────────────────────────────────────────────────────────
function Hero() {
  const ref = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] });
  const y = useTransform(scrollYProgress, [0, 1], ['0%', '30%']);
  const opacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section ref={ref} className="relative h-screen min-h-[600px] overflow-hidden">
      {/* Parallax BG */}
      <motion.div style={{ y }} className="absolute inset-0">
        <img
          src={IMAGES.CUB_4}
          alt="小狮子"
          className="w-full h-full object-cover object-center scale-110"
        />
        {/* gradient overlays */}
        <div className="absolute inset-0 bg-gradient-to-t from-[oklch(0.18_0.05_55)] via-[oklch(0.18_0.05_55/0.35)] to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-r from-[oklch(0.18_0.05_55/0.6)] to-transparent" />
      </motion.div>

      {/* Content */}
      <motion.div
        style={{ opacity }}
        className="relative z-10 h-full flex flex-col justify-center px-8 md:px-20 max-w-3xl"
      >
        <motion.p
          variants={fadeUp} initial="hidden" animate="visible" custom={0}
          className="text-sm font-bold uppercase tracking-widest mb-4"
          style={{ color: 'var(--gold)' }}
        >
          🌍 非洲草原 · 百兽之王
        </motion.p>
        <motion.h1
          variants={fadeUp} initial="hidden" animate="visible" custom={1}
          className="text-5xl md:text-7xl font-black leading-none mb-6 text-white"
          style={{ fontFamily: "'Playfair Display', serif" }}
        >
          小狮子，
          <br />
          <span style={{ color: 'var(--gold)' }}>大世界</span>
        </motion.h1>
        <motion.p
          variants={fadeUp} initial="hidden" animate="visible" custom={2}
          className="text-lg text-white/75 leading-relaxed mb-10 max-w-lg"
        >
          从懵懂幼崽到草原雄狮，每一步成长都是一首壮阔的诗篇。
          走进小狮子的世界，感受生命最纯粹的力量与温情。
        </motion.p>
        <motion.div
          variants={fadeUp} initial="hidden" animate="visible" custom={3}
          className="flex gap-4 flex-wrap"
        >
          <button
            onClick={() => scrollTo('about')}
            className="px-8 py-3 rounded-full font-bold text-white text-sm shadow-lg transition-all hover:scale-105 active:scale-95"
            style={{ background: 'var(--amber)' }}
          >
            探索世界
          </button>
          <button
            onClick={() => scrollTo('gallery')}
            className="px-8 py-3 rounded-full font-bold text-sm border border-white/40 text-white backdrop-blur-sm hover:bg-white/10 transition-all"
          >
            查看图廊
          </button>
        </motion.div>
      </motion.div>

      {/* Scroll cue */}
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 2 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-1 cursor-pointer"
        onClick={() => scrollTo('about')}
      >
        <span className="text-white/50 text-xs font-medium tracking-widest uppercase">向下滚动</span>
        <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 1.5 }}>
          <ChevronDown className="text-white/50 w-5 h-5" />
        </motion.div>
      </motion.div>
    </section>
  );
}

// ── About ─────────────────────────────────────────────────────────────────────
function About() {
  return (
    <section id="about" className="py-24 px-6 md:px-16 max-w-7xl mx-auto">
      <div className="grid md:grid-cols-2 gap-16 items-center">
        {/* Images mosaic */}
        <motion.div
          variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}
          className="grid grid-cols-2 gap-4"
        >
          <div className="space-y-4">
            <img src={IMAGES.CUB_5} alt="小狮子" className="w-full h-64 object-cover rounded-2xl shadow-xl" />
            <img src={IMAGES.CUB_9} alt="小狮子" className="w-full h-40 object-cover rounded-2xl shadow-lg" />
          </div>
          <div className="space-y-4 mt-10">
            <img src={IMAGES.CUB_7} alt="小狮子" className="w-full h-40 object-cover rounded-2xl shadow-lg" />
            <img src={IMAGES.CUB_3} alt="小狮子" className="w-full h-64 object-cover rounded-2xl shadow-xl" />
          </div>
        </motion.div>

        {/* Text */}
        <motion.div
          variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={1}
          className="space-y-6"
        >
          <p className="text-sm font-bold uppercase tracking-widest" style={{ color: 'var(--amber)' }}>
            认识小狮子
          </p>
          <h2 className="text-4xl md:text-5xl font-black leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
            萌动之心，
            <br />
            王者血脉
          </h2>
          <div className="w-12 h-1 rounded-full" style={{ background: 'var(--gold)' }} />
          <p className="text-muted-foreground text-lg leading-relaxed">
            刚出生的小狮子只有 1–2 公斤，双眼紧闭，全身布满迷彩花纹。
            它们在妈妈温暖的怀抱中慢慢睁眼，用好奇又懵懂的眼神打量这个广袤的世界。
          </p>
          <p className="text-muted-foreground leading-relaxed">
            从颤颤巍巍地学步，到在草地上追逐嬉戏；从远远观望妈妈捕猎，
            到独立纵横草原——每一步都写满了成长的故事。
            这是大自然最壮阔也最温柔的诗篇。
          </p>
          <div className="grid grid-cols-3 gap-4 pt-2">
            {[
              { value: '4 年', label: '成年年龄' },
              { value: '8km', label: '吼声传播距离' },
              { value: '80km/h', label: '最高奔跑速度' },
            ].map(({ value, label }) => (
              <div key={label} className="text-center p-4 rounded-2xl bg-secondary/60">
                <div className="text-2xl font-black" style={{ color: 'var(--amber)' }}>{value}</div>
                <div className="text-xs text-muted-foreground mt-1">{label}</div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}

// ── Facts ─────────────────────────────────────────────────────────────────────
function Facts() {
  return (
    <section id="facts" className="py-24 px-6 md:px-16" style={{ background: 'oklch(0.22 0.04 55)' }}>
      <div className="max-w-7xl mx-auto">
        <motion.div
          variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}
          className="text-center mb-16"
        >
          <p className="text-sm font-bold uppercase tracking-widest mb-3" style={{ color: 'var(--gold)' }}>
            趣味知识
          </p>
          <h2 className="text-4xl md:text-5xl font-black text-white" style={{ fontFamily: "'Playfair Display', serif" }}>
            关于狮子，你不知道的秘密
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {FACTS.map(({ icon, title, desc, color, border }, i) => (
            <motion.div
              key={title}
              variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={i * 0.5}
              whileHover={{ y: -6, transition: { duration: 0.25 } }}
              className={`bg-gradient-to-br ${color} border ${border} rounded-3xl p-7 cursor-default backdrop-blur-sm`}
              style={{ background: 'oklch(0.28 0.04 55 / 0.8)' }}
            >
              <div className="mb-4" style={{ color: 'var(--gold)' }}>{icon}</div>
              <h3 className="text-xl font-black text-white mb-3">{title}</h3>
              <p className="text-white/65 text-sm leading-relaxed">{desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Timeline ──────────────────────────────────────────────────────────────────
function Timeline() {
  return (
    <section id="timeline" className="py-24 px-6 md:px-16 max-w-7xl mx-auto">
      <motion.div
        variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}
        className="text-center mb-16"
      >
        <p className="text-sm font-bold uppercase tracking-widest mb-3" style={{ color: 'var(--amber)' }}>
          成长历程
        </p>
        <h2 className="text-4xl md:text-5xl font-black" style={{ fontFamily: "'Playfair Display', serif" }}>
          从幼崽到<span style={{ color: 'var(--amber)' }}>草原之王</span>
        </h2>
      </motion.div>

      <div className="relative">
        {/* Center line */}
        <div className="absolute left-1/2 -translate-x-1/2 top-0 bottom-0 w-0.5 hidden md:block" style={{ background: 'var(--border)' }} />

        <div className="space-y-12">
          {TIMELINE.map(({ age, title, desc, img }, i) => {
            const isLeft = i % 2 === 0;
            return (
              <motion.div
                key={age}
                variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={i * 0.3}
                className={`md:grid md:grid-cols-2 md:gap-12 items-center ${!isLeft ? 'md:[&>*:first-child]:order-2' : ''}`}
              >
                {/* Card */}
                <div className={`mb-6 md:mb-0 ${isLeft ? 'md:text-right' : 'md:text-left'}`}>
                  <div
                    className="inline-block px-4 py-1 rounded-full text-xs font-black mb-3"
                    style={{ background: 'var(--amber)', color: 'white' }}
                  >
                    {age}
                  </div>
                  <h3 className="text-2xl font-black mb-2">{title}</h3>
                  <p className="text-muted-foreground leading-relaxed">{desc}</p>
                </div>

                {/* Image */}
                <div className="relative">
                  {/* dot on line */}
                  <div
                    className="absolute top-1/2 -translate-y-1/2 hidden md:block w-4 h-4 rounded-full border-4 z-10"
                    style={{
                      background: 'var(--amber)',
                      borderColor: 'var(--background)',
                      [isLeft ? 'right' : 'left']: '-2.25rem',
                    }}
                  />
                  <img
                    src={img}
                    alt={title}
                    className="w-full h-56 object-cover rounded-2xl shadow-lg"
                  />
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ── Gallery ───────────────────────────────────────────────────────────────────
function Gallery() {
  const [selected, setSelected] = useState<null | { src: string; label: string }>(null);

  return (
    <section id="gallery" className="py-24 px-6 md:px-16" style={{ background: 'oklch(0.93 0.03 72)' }}>
      <div className="max-w-7xl mx-auto">
        <motion.div
          variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}
          className="text-center mb-14"
        >
          <p className="text-sm font-bold uppercase tracking-widest mb-3" style={{ color: 'var(--amber)' }}>
            图片展廊
          </p>
          <h2 className="text-4xl md:text-5xl font-black" style={{ fontFamily: "'Playfair Display', serif" }}>
            每一帧，都是传说
          </h2>
        </motion.div>

        {/* Masonry-style grid */}
        <div className="columns-2 md:columns-3 gap-4 space-y-0">
          {GALLERY.map(({ src, label }, i) => (
            <motion.div
              key={src}
              variants={fadeIn} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={i * 0.3}
              className="break-inside-avoid mb-4 group cursor-pointer relative overflow-hidden rounded-2xl shadow-md"
              onClick={() => setSelected({ src, label })}
              whileHover={{ scale: 1.02 }}
              transition={{ duration: 0.25 }}
            >
              <img
                src={src}
                alt={label}
                className="w-full object-cover group-hover:scale-105 transition-transform duration-500"
                style={{ minHeight: i % 3 === 0 ? '280px' : i % 3 === 1 ? '200px' : '240px' }}
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all flex items-end p-4">
                <span className="text-white text-sm font-bold opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-300">
                  {label}
                </span>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Lightbox */}
      {selected && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }}
          className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center p-6"
          onClick={() => setSelected(null)}
        >
          <motion.div
            initial={{ scale: 0.85, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
            className="relative max-w-3xl w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <img src={selected.src} alt={selected.label} className="w-full rounded-3xl shadow-2xl object-cover" />
            <div className="mt-3 text-center text-white font-bold text-lg">{selected.label}</div>
            <button
              onClick={() => setSelected(null)}
              className="absolute -top-4 -right-4 w-9 h-9 rounded-full bg-white text-black font-black text-sm flex items-center justify-center shadow-lg hover:scale-110 transition-transform"
            >
              ✕
            </button>
          </motion.div>
        </motion.div>
      )}
    </section>
  );
}

// ── Footer ────────────────────────────────────────────────────────────────────
function Footer() {
  return (
    <footer
      className="py-16 px-8 text-center"
      style={{ background: 'oklch(0.18 0.05 55)' }}
    >
      <motion.div
        variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}
        className="max-w-2xl mx-auto"
      >
        <div className="text-5xl mb-4">🦁</div>
        <h3
          className="text-3xl font-black text-white mb-3"
          style={{ fontFamily: "'Playfair Display', serif", color: 'var(--gold)' }}
        >
          小狮子王国
        </h3>
        <p className="text-white/50 mb-8 leading-relaxed">
          草原上的每一只幼崽，都藏着一个关于力量与温情的故事。
          <br />
          愿我们永远保持对世界的好奇与热爱。🌍
        </p>
        <div className="flex justify-center gap-6 text-white/30 text-sm">
          {['关于小狮', '趣味知识', '成长历程', '图片展廊'].map((t) => (
            <span key={t}>{t}</span>
          ))}
        </div>
        <div className="mt-8 text-white/20 text-xs">
          © 2026 小狮子王国 · 用爱记录每一步成长
        </div>
      </motion.div>
    </footer>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────
export default function Home() {
  return (
    <div className="overflow-x-hidden">
      <Navbar />
      <Hero />
      <About />
      <Facts />
      <Timeline />
      <Gallery />
      <Footer />
    </div>
  );
}
